import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Building2, Briefcase, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { getJob, getCompanies, submitJobApplication } from '../lib/api';
import { BackLink, CardShell, EmptyState, LoadingScreen, SectionTag, TealButton } from '../components/common/BrandElements';

const fieldClass = 'mt-2 rounded-2xl border-raad-teal/15 bg-raad-porcelain text-raad-charcoal placeholder:text-raad-graphite/35 focus-visible:ring-raad-teal';

const JobApplicationPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [job, setJob] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', cover_letter: '' });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [jobRes, companiesRes] = await Promise.all([getJob(jobId), getCompanies()]);
        setJob(jobRes.data);
        setCompanies(companiesRes.data);
      } catch (error) {
        console.error('Error loading job:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [jobId]);

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? t(company.name_ar, company.name_en) : '';
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error(t('يرجى ملء جميع الحقول المطلوبة', 'Please fill all required fields'));
      return;
    }

    setSubmitting(true);
    try {
      await submitJobApplication({ job_id: jobId, ...formData });
      toast.success(t('تم إرسال طلبك بنجاح! سنتواصل معك قريباً', 'Your application has been submitted! We will contact you soon'));
      navigate('/careers');
    } catch (error) {
      toast.error(t('حدث خطأ أثناء إرسال الطلب', 'Error submitting application'));
    } finally {
      setSubmitting(false);
    }
  };

  const jobTypeLabels = {
    full_time: { ar: 'دوام كامل', en: 'Full Time' },
    part_time: { ar: 'دوام جزئي', en: 'Part Time' },
    contract: { ar: 'عقد', en: 'Contract' }
  };

  if (loading) return <LoadingScreen />;

  if (!job) {
    return (
      <div className="min-h-screen bg-raad-porcelain pt-40">
        <EmptyState
          title={t('الوظيفة غير موجودة', 'Job Not Found')}
          action={<TealButton to="/careers">{t('العودة للوظائف', 'Back to Careers')}</TealButton>}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-raad-porcelain" data-testid="job-application-page">
      <section className="relative overflow-hidden pt-36 pb-14 lg:pt-40">
        <div className="brand-grid absolute inset-0 opacity-55" />
        <div className="container relative z-10 mx-auto max-w-6xl px-6">
          <BackLink to="/careers" label={t('العودة للوظائف', 'Back to Careers')} isRTL={isRTL} />
          <motion.header initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} className="mt-10">
            <SectionTag>{t('طلب توظيف', 'Job Application')}</SectionTag>
            <h1 className="mt-5 text-4xl font-black leading-tight text-raad-charcoal sm:text-5xl lg:text-6xl text-balance">
              {t(job.title_ar, job.title_en)}
            </h1>
          </motion.header>
        </div>
      </section>

      <section className="pb-24">
        <div className="container mx-auto grid max-w-6xl gap-8 px-6 lg:grid-cols-[360px_1fr]">
          <motion.aside initial={{ opacity: 0, x: isRTL ? 30 : -30 }} animate={{ opacity: 1, x: 0 }}>
            <CardShell className="sticky top-32 p-7">
              <div className="relative z-10">
                <h2 className="text-2xl font-black text-raad-charcoal">{t('تفاصيل الوظيفة', 'Job Details')}</h2>
                <div className="mt-6 space-y-4 text-sm font-semibold text-raad-graphite/70">
                  {job.company_id && (
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-raad-teal" />
                      {getCompanyName(job.company_id)}
                    </div>
                  )}
                  {(job.location_ar || job.location_en) && (
                    <div className="flex items-center gap-3">
                      <MapPin className="h-5 w-5 text-raad-teal" />
                      {t(job.location_ar, job.location_en)}
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Briefcase className="h-5 w-5 text-raad-teal" />
                    {t(jobTypeLabels[job.job_type]?.ar, jobTypeLabels[job.job_type]?.en)}
                  </div>
                  {job.closing_date && (
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-raad-teal" />
                      {t('ينتهي', 'Closes')}: {new Date(job.closing_date).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}
                    </div>
                  )}
                </div>

                {(job.description_ar || job.description_en) && (
                  <div className="mt-7 border-t border-raad-teal/15 pt-6">
                    <h3 className="font-black text-raad-charcoal">{t('الوصف', 'Description')}</h3>
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-7 text-raad-graphite/65">{t(job.description_ar, job.description_en)}</p>
                  </div>
                )}

                {(job.requirements_ar || job.requirements_en) && (
                  <div className="mt-6 border-t border-raad-teal/15 pt-6">
                    <h3 className="font-black text-raad-charcoal">{t('المتطلبات', 'Requirements')}</h3>
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-7 text-raad-graphite/65">{t(job.requirements_ar, job.requirements_en)}</p>
                  </div>
                )}
              </div>
            </CardShell>
          </motion.aside>

          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <form onSubmit={handleSubmit} className="rounded-[2rem] border border-raad-teal/15 bg-white p-7 shadow-soft md:p-9">
              <SectionTag>{t('بيانات المتقدم', 'Applicant Data')}</SectionTag>
              <h2 className="mt-4 text-3xl font-black text-raad-charcoal">
                {t('تقديم طلب التوظيف', 'Submit Application')}
              </h2>

              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                <div>
                  <Label htmlFor="name" className="font-bold text-raad-charcoal">{t('الاسم الكامل', 'Full Name')} *</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange} className={fieldClass} placeholder={t('أدخل اسمك الكامل', 'Enter your full name')} data-testid="application-name-input" />
                </div>
                <div>
                  <Label htmlFor="phone" className="font-bold text-raad-charcoal">{t('رقم الهاتف', 'Phone Number')} *</Label>
                  <Input id="phone" name="phone" dir="ltr" value={formData.phone} onChange={handleChange} className={`${fieldClass} phone-ltr`} placeholder={t('أدخل رقم هاتفك', 'Enter your phone')} data-testid="application-phone-input" />
                </div>
              </div>

              <div className="mt-6">
                <Label htmlFor="email" className="font-bold text-raad-charcoal">{t('البريد الإلكتروني', 'Email Address')} *</Label>
                <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} className={fieldClass} placeholder={t('أدخل بريدك الإلكتروني', 'Enter your email')} data-testid="application-email-input" />
              </div>

              <div className="mt-6">
                <Label htmlFor="cover_letter" className="font-bold text-raad-charcoal">{t('رسالة التقديم', 'Cover Letter')}</Label>
                <Textarea id="cover_letter" name="cover_letter" value={formData.cover_letter} onChange={handleChange} className={`${fieldClass} min-h-[220px]`} placeholder={t('اكتب رسالة توضح سبب رغبتك في هذه الوظيفة وخبراتك ذات الصلة', 'Write a message explaining why you want this job and your relevant experience')} data-testid="application-cover-input" />
              </div>

              <Button type="submit" disabled={submitting} className="mt-7 w-full rounded-full bg-raad-charcoal py-6 text-white hover:bg-raad-teal" data-testid="application-submit-btn">
                {submitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 me-2" />
                    {t('إرسال الطلب', 'Submit Application')}
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default JobApplicationPage;
